import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
} from '@mui/material';
import axiosInstance from '../api/axiosInstance';

export default function PlaceOptionsDialog({
  open,
  onClose,
  latitude,
  longitude,
  initialReverseGeocode,
  initialSelectedId,
  title = 'Select Place',
  subtitle = 'Multiple places found at this location. Please select the most appropriate one:',
  enableCreate = true,
  autoOpenCreate = false,
  onConfirm,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reverseGeocode, setReverseGeocode] = useState(initialReverseGeocode || null);
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);

  // Create place dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [placeNameInput, setPlaceNameInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editedLocality, setEditedLocality] = useState({
    area: '',
    village: '',
    city: '',
    state: '',
    country: 'Cyprus',
  });

  // Category autocomplete/validation (ported from SeeContent)
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const categorySearchTimer = useRef(null);
  const blurTimeoutRef = useRef(null);
  const [showNoMatches, setShowNoMatches] = useState(false);
  const [isCategoryValid, setIsCategoryValid] = useState(null); // null | boolean
  const [categoryTouched, setCategoryTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setSelectedId(initialSelectedId || initialReverseGeocode?.place?.id || null);

    if (initialReverseGeocode) {
      setReverseGeocode(initialReverseGeocode);
      if (autoOpenCreate) setCreateOpen(true);
      return;
    }

    const hasCoords = typeof latitude === 'number' && Number.isFinite(latitude)
      && typeof longitude === 'number' && Number.isFinite(longitude);
    if (!hasCoords) {
      setReverseGeocode(null);
      setError('Missing coordinates');
      return;
    }

    let cancelled = false;
    const run = async () => {
      setBusy(true);
      try {
        const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude, longitude });
        if (cancelled) return;
        setReverseGeocode(data || null);
        setSelectedId(data?.place?.id || null);
        if (autoOpenCreate) setCreateOpen(true);
      } catch (e) {
        if (cancelled) return;
        setError('Failed to load places');
        setReverseGeocode(null);
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    run();

    return () => { cancelled = true; };
  }, [open, latitude, longitude, initialReverseGeocode, initialSelectedId, autoOpenCreate]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      try { if (categorySearchTimer.current) clearTimeout(categorySearchTimer.current); } catch (_) { /* ignore */ }
      try { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current); } catch (_) { /* ignore */ }
    };
  }, []);

  const options = useMemo(() => {
    if (!reverseGeocode) return [];
    const baseLoc = reverseGeocode.locality || {};
    return [reverseGeocode.place, ...(reverseGeocode.alternatives || [])]
      .filter(Boolean)
      .map((o) => ({ ...o, locality: o.locality || baseLoc }));
  }, [reverseGeocode]);

  // If reverse-geocode produced zero place options, the user has nothing to select.
  // In that case we require a place name (and category) so a post doesn't end up with an unnamed place.
  const requirePlaceName = options.length === 0;

  const hasCoords = typeof latitude === 'number' && Number.isFinite(latitude)
    && typeof longitude === 'number' && Number.isFinite(longitude);

  const darkTextFieldSx = {
    backgroundColor: '#1f1f1f',
    borderRadius: '10px',
    color: '#f5f5f5',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1f1f1f',
      borderRadius: '10px',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#48c9b0' },
    },
    '& .MuiInputBase-root': {
      color: '#f5f5f5',
      fontSize: '14px',
      padding: '4px 10px',
    },
    '& .MuiInputBase-input': {
      padding: '8px 4px',
    },
    '& .MuiFormLabel-root': { color: '#cbd5e1' },
    '& .MuiFormLabel-root.Mui-focused': { color: '#48c9b0' },
    '& .MuiFormHelperText-root': { color: '#cbd5e1', marginLeft: 0, marginRight: 0 },
    '& .MuiInputBase-input::placeholder': { color: '#d1d5db', opacity: 1 },
    '& .MuiInputBase-root.Mui-disabled': {
      color: '#333',
      WebkitTextFillColor: '#333',
    },
  };

  // Place Name / Category are not `size="small"` by default, and our custom padding can
  // make the label/placeholder look vertically off. Use `size="small"` + reset padding.
  const topTextFieldSx = {
    ...darkTextFieldSx,
    '& .MuiInputBase-root': {
      ...(darkTextFieldSx['& .MuiInputBase-root'] || {}),
      padding: 0,
    },
    '& .MuiInputBase-input': {
      padding: '10.5px 14px',
    },
  };

  // Match SeeContent: determine if the create form is unchanged from the cached-prefill
  // (we intentionally treat original place name/category as empty strings).
  const isInputUnchangedFromCache = () => {
    if (!reverseGeocode) return false;

    const locality = reverseGeocode?.locality || {};
    const context = locality.context || {};
    const place = reverseGeocode?.place || {};

    const originalLocality = {
      area: locality.area ||
        context.area_name ||
        (locality.type === 'area' ? locality.name : '') ||
        place.locality?.area || '',
      village: locality.village ||
        context.village_name ||
        (locality.type === 'village' ? locality.name : '') ||
        place.locality?.village || '',
      city: locality.city ||
        context.city_name ||
        (locality.type === 'city' ? locality.name : '') ||
        place.locality?.city || '',
      state: locality.state ||
        context.state_name ||
        (locality.type === 'state' ? locality.name : '') ||
        place.locality?.state || '',
      country: locality.country ||
        context.country_name ||
        (locality.type === 'country' ? locality.name : '') ||
        place.locality?.country ||
        'Cyprus',
    };

    const originalPlaceName = '';
    const originalCategory = '';

    const currentPlaceName = (placeNameInput || '').trim();
    const currentCategory = (selectedCategory || '').trim();

    const localityMatches = (
      (editedLocality?.area || '').trim() === originalLocality.area &&
      (editedLocality?.village || '').trim() === originalLocality.village &&
      (editedLocality?.city || '').trim() === originalLocality.city &&
      (editedLocality?.state || '').trim() === originalLocality.state &&
      (editedLocality?.country || '').trim() === originalLocality.country
    );

    const placeMatches = (
      currentPlaceName === originalPlaceName &&
      currentCategory === originalCategory
    );

    return localityMatches && placeMatches;
  };

  const prefillCreateForm = () => {
    const loc = reverseGeocode?.locality || {};
    const context = loc.context || {};
    const place = reverseGeocode?.place || {};

    setPlaceNameInput(place?.name || '');
    setSelectedCategory(place?.category || '');

    setEditedLocality({
      area: loc.area || context.area_name || (loc.type === 'area' ? loc.name : '') || place?.locality?.area || '',
      village: loc.village || context.village_name || (loc.type === 'village' ? loc.name : '') || place?.locality?.village || '',
      city: loc.city || context.city_name || (loc.type === 'city' ? loc.name : '') || place?.locality?.city || '',
      state: loc.state || context.state_name || (loc.type === 'state' ? loc.name : '') || place?.locality?.state || '',
      country: loc.country || context.country_name || (loc.type === 'country' ? loc.name : '') || place?.locality?.country || 'Cyprus',
    });

    setCategorySuggestions([]);
    setShowNoMatches(false);
    setIsCategoryValid(null);
    setCategoryTouched(false);
  };

  const searchCategories = (term) => {
    if (!term || !term.trim()) {
      setCategorySuggestions([]);
      setIsCategoryValid(null);
      return;
    }

    if (categorySearchTimer.current) clearTimeout(categorySearchTimer.current);

    setShowNoMatches(false);
    setCategoryTouched(true);

    categorySearchTimer.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get('/categories/search', { params: { q: term.trim(), limit: 20 } });
        if (!res || res.status !== 200) {
          setCategorySuggestions([]);
          return;
        }
        const items = res.data?.items || [];
        setCategorySuggestions(items);
        const typedLower = term.trim().toLowerCase();
        const isExact = items.some(it => (it.name || '').toLowerCase() === typedLower);
        setIsCategoryValid(isExact ? true : false);
        setShowNoMatches(items.length === 0);
      } catch (e) {
        setCategorySuggestions([]);
        setShowNoMatches(false);
        setIsCategoryValid(null);
      }
    }, 250);
  };

  const exactCheckCategory = async (term) => {
    if (!term || !term.trim()) {
      setShowNoMatches(false);
      setIsCategoryValid(null);
      return false;
    }
    try {
      const res = await axiosInstance.get('/categories/search', { params: { q: term.trim(), limit: 50 } });
      if (res && res.status === 200) {
        const items = res.data?.items || [];
        const match = items.find(it => (it.name || '').toLowerCase() === term.trim().toLowerCase());
        if (match) {
          setSelectedCategory(match.name);
          setCategorySuggestions([]);
          setShowNoMatches(false);
          setIsCategoryValid(true);
          return true;
        }
      }
    } catch (e) {
      // ignore
    }
    setShowNoMatches(true);
    setCategorySuggestions([]);
    setIsCategoryValid(false);
    return false;
  };

  const showCategoryInlineError = !!(categoryTouched && (selectedCategory || '').trim() && isCategoryValid === false);

  const handleConfirm = async () => {
    if (!selectedId || !reverseGeocode) return;
    const selectedPlace = options.find((o) => o.id === selectedId);
    if (!selectedPlace) return;

    setBusy(true);
    try {
      const baseLoc = reverseGeocode.locality || null;
      const next = {
        ...reverseGeocode,
        place: selectedPlace,
        locality: selectedPlace.locality || baseLoc,
        alternatives: [],
      };
      onConfirm?.({ reverseGeocodeResult: next, selectedPlace, kind: 'select' });
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!hasCoords) {
      setError('Missing coordinates');
      return;
    }

    const placeName = (placeNameInput || '').trim();
    const category = (selectedCategory || '').trim();

    if (requirePlaceName && !placeName) {
      setError('Place name is required when no places were found for this location.');
      return;
    }

    if (isInputUnchangedFromCache()) {
      setError('Please modify at least one field or select an existing place suggestion.');
      return;
    }

    if (category) {
      const ok = await exactCheckCategory(category);
      if (!ok) {
        setError('Please select a category from the suggestions.');
        return;
      }
    }

    if ((placeName && !category) || (!placeName && category)) {
      setError('Both place name and category are required when creating a new place.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await axiosInstance.post('/places/create', {
        name: placeName || null,
        category: category || 'user_generated',
        latitude,
        longitude,
        locality: {
          area: editedLocality.area || '',
          village: editedLocality.village || '',
          city: editedLocality.city || '',
          state: editedLocality.state || '',
          country: editedLocality.country || 'Cyprus',
        }
      });

      const placeId = res.data?.placeId;
      let nextReverseGeocode = null;

      if (res.data?.place) {
        nextReverseGeocode = {
          place: res.data.place,
          locality: res.data.locality || null,
          fromCache: false,
          alternatives: [],
        };
      } else if (res.data?.locality) {
        nextReverseGeocode = {
          place: null,
          locality: {
            name: res.data.locality.city || res.data.locality.village || res.data.locality.area || res.data.locality.state || res.data.locality.country || 'Locality',
            type: (res.data.locality.area && 'area') || (res.data.locality.village && 'village') || (res.data.locality.city && 'city') || (res.data.locality.state && 'state') || 'country',
            context: {
              city_name: res.data.locality.city || null,
              state_name: res.data.locality.state || null,
              country_name: res.data.locality.country || null,
              village_name: res.data.locality.village || null,
              area_name: res.data.locality.area || null,
            }
          },
          fromCache: false,
          alternatives: [],
        };
      }

      if (nextReverseGeocode) {
        setReverseGeocode(nextReverseGeocode);
      }

      // Match SeeContent: refetch server-side reverse geocode to pick up locked selection/cache.
      try {
        const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude, longitude });
        if (data) {
          nextReverseGeocode = data;
          setReverseGeocode(data);
        }
      } catch (_) {
        // ignore
      }

      onConfirm?.({ reverseGeocodeResult: nextReverseGeocode, selectedPlace: nextReverseGeocode?.place || null, placeId, kind: 'create' });
      setCreateOpen(false);
      onClose?.();
    } catch (e) {
      if (e?.response?.status === 400 && e?.response?.data?.error === 'invalid_category') {
        setError('Please select a valid category from the suggestions.');
        setShowNoMatches(true);
        setCategorySuggestions([]);
      } else {
        setError('Failed to create new place. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={!!open}
        onClose={() => { if (!busy) onClose?.(); }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#181818',
              color: 'white',
              borderRadius: '12px',
              padding: '14px',
              maxWidth: '600px',
              width: '100%',
            }
          }
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {busy && !reverseGeocode && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 18 }}>
                <CircularProgress size={26} sx={{ color: '#fff' }} />
              </div>
            )}

            {error && (
              <div style={{ color: '#fca5a5', fontSize: 14 }}>{error}</div>
            )}

            {options.length > 0 ? (
              <>
                <div style={{ fontSize: 14, color: '#bbb' }}>{subtitle}</div>
                <List dense>
                  {options.map((option) => (
                    <ListItem
                      key={option.id}
                      style={{
                        border: selectedId === option.id ? '2px solid #48c9b0' : '1px solid #333',
                        borderRadius: 8,
                        marginBottom: 8,
                        padding: 8,
                        background: '#121212'
                      }}
                    >
                      <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => setSelectedId(option.id)}
                        >
                          <ListItemText
                            primary={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600, color: '#fff' }}>{option.name}</span>
                                {option.source_type && option.source_type !== 'pending_place' && (
                                  <span style={{
                                    fontSize: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    background: '#0f2d25',
                                    color: '#6ee7b7',
                                    border: '1px solid #065f46'
                                  }}>
                                    VERIFIED
                                  </span>
                                )}
                              </div>
                            }
                            secondary={
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {option.category && <div style={{ fontSize: 13, color: '#ddd' }}>Category: {option.category}</div>}
                                {option.distance && <div style={{ fontSize: 12, color: '#999' }}>~ {Math.round(option.distance)}m away</div>}
                                {option.locality && (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                                    {option.locality.area && (
                                      <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Area: {option.locality.area}</span>
                                    )}
                                    {option.locality.village && (
                                      <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Village: {option.locality.village}</span>
                                    )}
                                    {option.locality.city && (
                                      <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>City: {option.locality.city}</span>
                                    )}
                                    {option.locality.state && (
                                      <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>State: {option.locality.state}</span>
                                    )}
                                    {option.locality.country && (
                                      <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Country: {option.locality.country}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              !busy && <div style={{ color: '#bbb', fontSize: 14 }}>No places found for this location.</div>
            )}

            {enableCreate && (
              <Button
                variant="outlined"
                onClick={() => {
                  prefillCreateForm();
                  setError(null);
                  setCreateOpen(true);
                }}
                disabled={busy || !hasCoords}
                sx={{ alignSelf: 'flex-start', marginTop: 1, borderColor: '#48c9b0', color: '#e5e7eb', '&:hover': { borderColor: '#3fb6a0' } }}
              >
                + Create New Place
              </Button>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { if (!busy) onClose?.(); }} disabled={busy} sx={{ color: '#fff' }}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !selectedId || options.length === 0}
            variant="contained"
            sx={{ backgroundColor: '#48c9b0', color: '#181818', '&:hover': { backgroundColor: '#3fb6a0' } }}
          >
            Use Selected Place
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => { if (!busy) setCreateOpen(false); }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#181818',
              color: 'white',
              borderRadius: '12px',
              padding: '14px',
              maxWidth: '600px',
              width: '100%'
            }
          }
        }}
      >
        <DialogTitle>Create New Place</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <TextField
              label={requirePlaceName ? 'Place Name' : 'Place Name (optional)'}
              value={placeNameInput}
              onChange={(e) => setPlaceNameInput(e.target.value)}
              fullWidth
              size="small"
              sx={topTextFieldSx}
            />

            {requirePlaceName && !(placeNameInput || '').trim() && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: -5 }}>
                Place name is required when no places are found.
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <TextField
                label="Category"
                value={selectedCategory}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCategory(val);
                  if (!val.trim()) {
                    setIsCategoryValid(null);
                    setShowNoMatches(false);
                    setCategorySuggestions([]);
                  } else {
                    searchCategories(val);
                  }
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    await exactCheckCategory(selectedCategory);
                  }
                }}
                onBlur={() => {
                  blurTimeoutRef.current = setTimeout(() => {
                    setCategorySuggestions([]);
                    setShowNoMatches(false);
                  }, 150);
                }}
                onFocus={() => {
                  if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
                  if ((selectedCategory || '').trim()) searchCategories(selectedCategory);
                }}
                fullWidth
                placeholder="e.g. restaurant, cafe, shop, hotel..."
                error={showCategoryInlineError}
                inputProps={{ 'aria-autocomplete': 'list', autoComplete: 'off' }}
                size="small"
                sx={topTextFieldSx}
              />

              {((selectedCategory || '').trim() && (((categorySuggestions || []).length > 0) || showNoMatches)) && (
                <div
                  aria-label="Category suggestions"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '100%',
                    zIndex: 1200,
                    background: '#1f1f1f',
                    border: '1px solid #333',
                    borderRadius: 4,
                    maxHeight: 220,
                    overflow: 'auto',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.3)'
                  }}
                >
                  {((categorySuggestions || []).length === 0 && showNoMatches) ? (
                    <div style={{ padding: 8, color: '#bbb' }}>No matches</div>
                  ) : (
                    (categorySuggestions || []).map((opt) => (
                      <div
                        key={opt.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedCategory(opt.name);
                          setCategorySuggestions([]);
                          setShowNoMatches(false);
                          setIsCategoryValid(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setSelectedCategory(opt.name);
                            setCategorySuggestions([]);
                            setShowNoMatches(false);
                            setIsCategoryValid(true);
                          }
                        }}
                        style={{ padding: 8, borderBottom: '1px solid #333', cursor: 'pointer', color: '#e5e7eb' }}
                      >
                        {opt.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 8 }}>Locality Information</div>

            {(reverseGeocode?.locality || reverseGeocode?.fromCache) && (
              <div style={{
                fontSize: 14,
                color: '#6ee7b7',
                backgroundColor: '#0f2d25',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #065f46'
              }}>
                Locality information prefilled from {reverseGeocode?.fromCache ? 'cached' : 'recent'} location lookup
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <TextField
                label="Area"
                value={editedLocality.area || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, area: e.target.value }))}
                placeholder="Enter area"
                size="small"
                sx={darkTextFieldSx}
              />
              <TextField
                label="Village"
                value={editedLocality.village || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, village: e.target.value }))}
                placeholder="Enter village"
                size="small"
                sx={darkTextFieldSx}
              />
              <TextField
                label="City"
                value={editedLocality.city || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Enter city"
                size="small"
                sx={darkTextFieldSx}
              />
              <TextField
                label="State" // province, or district
                value={editedLocality.state || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, state: e.target.value }))}
                placeholder="Enter state"
                size="small"
                sx={darkTextFieldSx}
              />
            </div>

            <TextField
              label="Country"
              value={editedLocality.country}
              disabled
              placeholder="Country"
              size="small"
              sx={{
                ...darkTextFieldSx,
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                  ...(darkTextFieldSx['& .MuiOutlinedInput-root'] || {}),
                  backgroundColor: 'transparent',
                },
                '& .MuiInputBase-root': {
                  ...(darkTextFieldSx['& .MuiInputBase-root'] || {}),
                  backgroundColor: 'transparent',
                },
                '& .MuiInputBase-root.Mui-disabled': {
                  color: '#cbd5e1',
                  WebkitTextFillColor: '#cbd5e1',
                },
              }}
            />

            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 8 }}>
              Fill in any missing locality information to help build our community database.
              This information helps other users find places in your area.
            </div>

            {isInputUnchangedFromCache() && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>
                Please modify at least one field or select an existing place suggestion.
              </div>
            )}

            {(((placeNameInput || '').trim() && !(selectedCategory || '').trim()) || (!(placeNameInput || '').trim() && (selectedCategory || '').trim())) && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>
                Both place name and category are required when creating a new place.
              </div>
            )}

            {error && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 4 }}>{error}</div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={busy} sx={{ color: '#fff' }}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={
              busy
              || !hasCoords
              || isInputUnchangedFromCache()
              || (requirePlaceName && !(placeNameInput || '').trim())
              || showCategoryInlineError
              || (((placeNameInput || '').trim() && !(selectedCategory || '').trim()) || (!(placeNameInput || '').trim() && (selectedCategory || '').trim()))
            }
            variant="contained"
            sx={{ backgroundColor: '#48c9b0', color: '#181818', '&:hover': { backgroundColor: '#3fb6a0' } }}
          >
            {requirePlaceName ? 'Create Place' : ((placeNameInput || '').trim() ? 'Create Place' : 'Update Locality')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
